import React, { useEffect, useState } from "react";
import useAxios from "../../../hooks/useAxios";
import "./StudentClassPage.css";
import { Box, CircularProgress, Container, Typography, Button, useMediaQuery } from "@mui/material";
import Header from "../../../components/Header/Header";
import QuizCard from "../../../components/QuizComponents/Cards/QuizCard/QuizCard";
import ActivateAccountAlert from "../../../components/Auth/ActivateAccountAlert/ActivateAccountAlert";
import ContentHeader from "../../../components/common/ContentHeader/ContentHeader";
import StudentAddQuizModal from "../../../components/DashboardComponents/Modals/StudentAddQuizModal/StudentAddQuizModal";
import { useTheme } from "@mui/material/styles";
import StudentReportTable from "../../../components/DashboardComponents/StudentDashboard/StudentReportTable/StudentReportTable";
import { useParams } from "react-router-dom";

const navLinks = [
];

function StudentClassPage() {
    const { classId } = useParams();
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const axiosInstance = useAxios();
    const [isActivateAlertOpen, setIsActivateAlertOpen] = useState(false);
    const [isAddQuizModalOpen, setIsAddQuizModalOpen] = useState(false);
    const [reportData, setReportData] = useState([]);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

    // Determine initial display count based on screen size
    let initialDisplayCount = 3;
    if (isSmallScreen) {
        initialDisplayCount = 1;
    } else if (isMediumScreen) {
        initialDisplayCount = 2;
    }

    const [showAllNew, setShowAllNew] = useState(false);
    const [showAllRetake, setShowAllRetake] = useState(false);

    const fetchQuizzes = async () => {
        try {
            const response = await axiosInstance.get(`/classes/student/${classId}`);
            if (response.data.success) {
                const { class: classData } = response.data; // Destructure the class object
                const quizzesArray = classData.quizzes || []; // Fallback to empty array if undefined
                const sortedQuizzes = quizzesArray
                .map(q => ({ ...q, attempts_count: q.attempts_count ?? 0 })) // default to 0 if not present
                .sort((a, b) => a.attempts_count - b.attempts_count);

                setQuizzes(sortedQuizzes);
            } else {
                setError(response.data.message || "Failed to fetch Assessments.");
            }
        } catch (err) {
            setError(err.response?.data?.message || "An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const fetchReport = async () => {
        try {
            const response = await axiosInstance.get("/user/sessions");
            if (response.data.success) {
                setReportData(response.data.data);
            } else {
                setError(response.data.message || "Failed to fetch Assessments.");
            }
        } catch (err) {
            setError(err.response?.data?.message || "An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchQuizzes();
        fetchReport();
    }, []);

    const handleEditQuiz = (quizId) => {
        setIsActivateAlertOpen(true);
    };

    const handleDeleteQuiz = (quizId) => {
        setIsActivateAlertOpen(true);
    };

    const handleAddQuiz = () => {
        setIsAddQuizModalOpen(true);
    };

    const handleModalClose = (refresh = false) => {
        setIsAddQuizModalOpen(false);
        if (refresh) {
            fetchQuizzes();
        }
    };

    // Filter quizzes by attempts_count
    const newQuizzes = quizzes.filter(q => q.attempts_count === 0);
    const retakeQuizzes = quizzes.filter(q => q.attempts_count === 1);
    // Ignore quizzes where attempts_count > 1

    // Determine which quizzes to show for each category
    const displayedNewQuizzes = showAllNew ? newQuizzes : newQuizzes.slice(0, initialDisplayCount);
    const displayedRetakeQuizzes = showAllRetake ? retakeQuizzes : retakeQuizzes.slice(0, initialDisplayCount);

    const totalNewQuizzes = newQuizzes.length;
    const totalRetakeQuizzes = retakeQuizzes.length;

    const isSubsetNew = !showAllNew && totalNewQuizzes > displayedNewQuizzes.length;
    const isSubsetRetake = !showAllRetake && totalRetakeQuizzes > displayedRetakeQuizzes.length;

    let content;
    if (loading) {
        content = (
            <Box className="loading-container">
                <CircularProgress />
                <Typography variant="h6" className="loading-text">
                    Fetching Assessments...
                </Typography>
            </Box>
        );
    } else if (error) {
        content = (
            <Box className="error-container">
                <Typography variant="h6" color="error">
                    {error}
                </Typography>
            </Box>
        );
    } else {
        content = (
            <>
                {/* New Quizzes Section */}
                <ContentHeader
                    title="New Assessments"
                />
                <div className="quizzes-grid">
                    {displayedNewQuizzes.length !== 0 ? (
                        displayedNewQuizzes.map((quiz) => (
                            <QuizCard
                                key={quiz.id}
                                quiz={quiz}
                                isStudent={true}
                                onEditClick={() => handleEditQuiz(quiz.id)}
                                onDeleteClick={() => handleDeleteQuiz(quiz.id)}
                            />
                        ))
                    ) : (
                        <Typography>No new Assessments to show</Typography>
                    )}
                </div>
                {(isSubsetNew || (showAllNew && totalNewQuizzes > initialDisplayCount)) && (
                    <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                        <Typography variant="body2" sx={{ mr: 2 }}>
                            {showAllNew
                                ? `Showing all ${totalNewQuizzes} Assessments`
                                : `Showing ${displayedNewQuizzes.length} of ${totalNewQuizzes} Assessments`}
                        </Typography>
                        <Button
                            className="text-button"
                            onClick={() => setShowAllNew(!showAllNew)}
                        >
                            {showAllNew ? "View Less" : "View All"}
                        </Button>
                    </Box>
                )}

                {/* Retake Quizzes Section */}
                {retakeQuizzes.length > 0 && (
                    <div className="mt-5">
                        <ContentHeader
                            title="Retake Assessments"
                        />
                        <div className="quizzes-grid">
                            {displayedRetakeQuizzes.length !== 0 ? (
                                displayedRetakeQuizzes.map((quiz) => (
                                    <QuizCard
                                        key={quiz.id}
                                        quiz={quiz}
                                        isStudent={true}
                                        onEditClick={() => handleEditQuiz(quiz.id)}
                                        onDeleteClick={() => handleDeleteQuiz(quiz.id)}
                                    />
                                ))
                            ) : (
                                <Typography>No retake Assessments to show</Typography>
                            )}
                        </div>
                        {(isSubsetRetake || (showAllRetake && totalRetakeQuizzes > initialDisplayCount)) && (
                            <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                                <Typography variant="body2" sx={{ mr: 2 }}>
                                    {showAllRetake
                                        ? `Showing all ${totalRetakeQuizzes} Assessments`
                                        : `Showing ${displayedRetakeQuizzes.length} of ${totalRetakeQuizzes} Assessments`}
                                </Typography>
                                <Button
                                    className="text-button"
                                    onClick={() => setShowAllRetake(!showAllRetake)}
                                >
                                    {showAllRetake ? "View Less" : "View All"}
                                </Button>
                            </Box>
                        )}
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            <Header navLinks={navLinks} isScrollEffect={false} />
            <Container className="owner-quizzes-container page-container">
                {content}
                <ActivateAccountAlert
                    open={isActivateAlertOpen}
                    onClose={() => setIsActivateAlertOpen(false)}
                />
            </Container>
            <StudentAddQuizModal
                open={isAddQuizModalOpen}
                onClose={handleModalClose}
            />
            <StudentReportTable data={reportData} />
        </>
    );
}

export default StudentClassPage;
